// ============================================================================
// Auto-test numérique des règles et de leurs exemples (lot 1 de la refonte).
//
// Au chargement, chaque règle est mise à l'épreuve sur des valeurs tirées de
// façon DÉTERMINISTE (graine = identifiant de la règle) :
//   - identité   : les deux formes ont la même valeur ;
//   - inéquation : même vérité (équivalence) ou vérité conservée (implication) ;
//   - équation   : les solutions de l'une, trouvées numériquement en faisant varier
//                  une inconnue, vérifient l'autre (et réciproquement si équivalence).
// C'est une preuve PAR ÉCHANTILLONS ('sampled'), jamais une preuve exacte : elle
// attrape une règle fausse (« (a − b)² = a² − b² »), pas toutes. Les exemples d'une
// règle sont jugés de la même façon ; un exemple faux doit être RÉFUTÉ.
// L'appariement d'un exemple avec les formes de la règle viendra avec le
// prototype (lot 2) : il faut le moteur de réécriture.
// ============================================================================

import { close, cmpHolds, ev, type Node } from '../../tree';
import type { Sort } from '../types';
import type { FormRelation, ParsedText } from './forms';
import type { PreconditionKind, RuleKind } from './schema';

type Env = Record<string, number>;

/** Contraintes d'une variable à échantillonner. */
export interface VarDomain {
  sort: Sort;
  conds: PreconditionKind[];
}
export interface Sampler {
  domains: Record<string, VarDomain>;
  distinct: string[][];
}

export type SemanticResult =
  | { verdict: 'holds'; tested: number }
  | { verdict: 'fails'; env: Env }
  | { verdict: 'untestable' };

const REAL_POOL = [
  -9.7, -6.25, -4, -2.5, -1.3, -0.6, -0.25, 0, 0.4, 0.75, 1, 1.6, 2.2, 3, 4.5, 7.3, 11,
];
const INT_POOL = Array.from({ length: 19 }, (_, i) => i - 9);
const NAT_POOL = Array.from({ length: 13 }, (_, i) => i);

const SAMPLES = 24;
const MIN_TESTED = 6;
const ROOT_ENVS = 8;
const SCAN_LO = -25;
const SCAN_HI = 25;
const SCAN_STEPS = 1000;

/** FNV-1a 32 bits : graine stable d'une chaîne. */
export function seedOf(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
/** mulberry32 : générateur déterministe dans [0, 1). */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function satisfies(v: number, conds: readonly PreconditionKind[]): boolean {
  return conds.every((c) => {
    switch (c) {
      case 'integer':
        return Number.isInteger(v);
      case 'natural':
        return Number.isInteger(v) && v >= 0;
      case 'nonzero':
        return v !== 0;
      case 'positive':
        return v > 0;
      case 'negative':
        return v < 0;
      case 'nonnegative':
        return v >= 0;
      default:
        return true; // 'real', 'distinct' : traités ailleurs
    }
  });
}

function poolFor(d: VarDomain): number[] {
  const base =
    d.sort === 'natural' || d.conds.includes('natural')
      ? NAT_POOL
      : d.sort === 'integer' || d.conds.includes('integer')
        ? INT_POOL
        : REAL_POOL;
  return base.filter((v) => satisfies(v, d.conds));
}

function distinctOk(env: Env, groups: string[][]): boolean {
  return groups.every((g) => new Set(g.map((n) => env[n])).size === g.length);
}

/** `count` environnements tirés de façon déterministe (moins si les contraintes l'imposent). */
export function sampleEnvs(s: Sampler, count: number, seed: string): Env[] {
  const names = Object.keys(s.domains).sort();
  if (names.length === 0) return [{}];
  const pools = names.map((n) => poolFor(s.domains[n]!));
  if (pools.some((p) => p.length === 0)) return [];
  const rand = rng(seedOf(seed));
  const out: Env[] = [];
  for (let tries = 0; out.length < count && tries < count * 20; tries++) {
    const env: Env = {};
    names.forEach((n, i) => {
      const p = pools[i]!;
      env[n] = p[Math.floor(rand() * p.length)]!;
    });
    if (distinctOk(env, s.distinct)) out.push(env);
  }
  return out;
}

function val(t: Node, env: Env): number | null {
  const v = ev(t, env);
  return Number.isFinite(v) ? v : null;
}

/** Vérité d'une relation sous `env` (null : hors domaine). */
function truth(rel: FormRelation, sides: Node[], env: Env): boolean | null {
  const a = val(sides[0]!, env);
  const b = sides[1] ? val(sides[1], env) : null;
  if (a === null || b === null) return null;
  if (rel === '=') return close(a, b);
  return cmpHolds(rel as '<' | '>' | '≤' | '≥', a, b);
}

function residualSmall(sides: Node[], env: Env): boolean | null {
  const a = val(sides[0]!, env);
  const b = val(sides[1]!, env);
  if (a === null || b === null) return null;
  return Math.abs(a - b) <= 1e-6 * Math.max(1, Math.abs(a), Math.abs(b));
}

/** Racines de L − R quand `v` varie (les autres variables fixées par `env`). */
function rootsIn(sides: Node[], v: string, env: Env): number[] {
  const g = (t: number): number | null => {
    const e = { ...env, [v]: t };
    const a = val(sides[0]!, e);
    const b = val(sides[1]!, e);
    return a === null || b === null ? null : a - b;
  };
  const roots: number[] = [];
  const h = (SCAN_HI - SCAN_LO) / SCAN_STEPS;
  let pt = SCAN_LO;
  let pv = g(pt);
  for (let i = 1; i <= SCAN_STEPS; i++) {
    const t = SCAN_LO + i * h;
    const tv = g(t);
    if (pv !== null && pv === 0) roots.push(pt);
    else if (pv !== null && tv !== null && pv * tv < 0) {
      let lo = pt;
      let hi = t;
      let lv = pv;
      for (let k = 0; k < 80; k++) {
        const m = (lo + hi) / 2;
        const mv = g(m);
        if (mv === null) break;
        if (lv * mv <= 0) hi = m;
        else {
          lo = m;
          lv = mv;
        }
      }
      const r = (lo + hi) / 2;
      // un changement de signe à travers un pôle (1 ÷ x) n'est pas une racine
      if (residualSmall(sides, { ...env, [v]: r })) roots.push(r);
    }
    pt = t;
    pv = tv;
  }
  return roots;
}

/** Les solutions de `a` (en faisant varier une inconnue réelle) vérifient-elles `b` ? */
function rootsCarry(
  a: Node[],
  b: Node[],
  s: Sampler,
  seed: string,
): { verdict: 'holds'; tested: number } | { verdict: 'fails'; env: Env } {
  let tested = 0;
  const unknowns = Object.keys(s.domains)
    .sort()
    .filter((n) => {
      const d = s.domains[n]!;
      return (
        (d.sort === 'real' || d.sort === 'rational') &&
        !d.conds.some((c) => c === 'integer' || c === 'natural')
      );
    });
  for (const v of unknowns) {
    const others: Sampler = {
      domains: Object.fromEntries(Object.entries(s.domains).filter(([n]) => n !== v)),
      distinct: [],
    };
    for (const env of sampleEnvs(others, ROOT_ENVS, `${seed}#${v}`)) {
      for (const r of rootsIn(a, v, env)) {
        const at = { ...env, [v]: r };
        if (!satisfies(r, s.domains[v]!.conds) || !distinctOk(at, s.distinct)) continue;
        const ok = residualSmall(b, at);
        if (ok === null) continue;
        if (!ok) return { verdict: 'fails', env: at };
        tested++;
      }
    }
  }
  return { verdict: 'holds', tested };
}

/**
 * La transformation `from` → `to` respecte-t-elle la garantie de `kind` ?
 * `target` : expression (identité), équation ou inéquation.
 */
export function checkSemantics(
  kind: RuleKind,
  target: 'expression' | 'equation' | 'inequality',
  from: ParsedText,
  to: ParsedText,
  s: Sampler,
  seed: string,
): SemanticResult {
  const noVars = Object.keys(s.domains).length === 0;
  const need = noVars ? 1 : MIN_TESTED;

  if (target === 'equation' && !noVars) {
    const fwd = rootsCarry(from.sides, to.sides, s, `${seed}>`);
    if (fwd.verdict === 'fails') return fwd;
    if (kind === 'implication') return fwd.tested > 0 ? fwd : { verdict: 'untestable' };
    const back = rootsCarry(to.sides, from.sides, s, `${seed}<`);
    if (back.verdict === 'fails') return back;
    return fwd.tested > 0 && back.tested > 0
      ? { verdict: 'holds', tested: fwd.tested + back.tested }
      : { verdict: 'untestable' };
  }

  let tested = 0;
  for (const env of sampleEnvs(s, SAMPLES, seed)) {
    if (target === 'expression') {
      const a = val(from.sides[0]!, env);
      const b = val(to.sides[0]!, env);
      if (a === null || b === null) continue;
      tested++;
      if (!close(a, b)) return { verdict: 'fails', env };
    } else {
      const a = truth(from.relation, from.sides, env);
      const b = truth(to.relation, to.sides, env);
      if (a === null || b === null) continue;
      tested++;
      if (kind === 'implication' ? a && !b : a !== b) return { verdict: 'fails', env };
    }
  }
  return tested >= need ? { verdict: 'holds', tested } : { verdict: 'untestable' };
}
